import { extractAskQuestionFromMessage } from './findCachedAskConversation.js';

function truncateText(text, max = 80) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(1, max - 1))}…`;
}

function firstSourceImage(sources = []) {
  for (const source of sources) {
    const imageUrl = String(source?.imageUrl || '').trim();
    if (imageUrl) return imageUrl;
  }
  return '';
}

function firstSourceUrl(sources = []) {
  for (const source of sources) {
    const url = String(source?.url || '').trim();
    if (url) return url;
  }
  return '';
}

function findPrecedingUserMessage(messages, assistantMessageIndex) {
  for (let i = assistantMessageIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') return messages[i];
  }
  return null;
}

function findLatestAssistantMessageIndex(messages = []) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') return i;
  }
  return -1;
}

function findFirstUserMessage(messages = []) {
  return messages.find(message => message?.role === 'user') || null;
}

function findLatestAssistantMessage(messages = []) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') return messages[i];
  }
  return null;
}

/** Recommended ask/search prompts store the visible label on a dedicated line. */
export function extractPinQuestionTitle(userContent, { max = 80 } = {}) {
  const askQuestion = extractAskQuestionFromMessage(userContent);
  if (askQuestion) return truncateText(askQuestion, max);

  const searchMatch = String(userContent || '').match(/^Search the web for:\s*(.+)$/im);
  if (searchMatch?.[1]) return truncateText(searchMatch[1].trim(), max);

  return truncateText(userContent, max);
}

export function buildConversationPinPayload(conversation) {
  if (!conversation?.id) return null;
  const messages = conversation.messages || [];
  const firstUser = findFirstUserMessage(messages);
  const latestAssistant = findLatestAssistantMessage(messages);
  if (!firstUser) return null;

  const sources = latestAssistant?.sources || [];

  return {
    conversationId: String(conversation.id),
    pinKind: 'conversation',
    messageAt: '',
    title: extractPinQuestionTitle(firstUser.content) || 'Board AI chat',
    snippet: truncateText(latestAssistant?.content || '', 180),
    imageUrl: firstSourceImage(sources),
    sourceUrl: firstSourceUrl(sources),
  };
}

export function buildLatestExchangePinPayload(conversation) {
  if (!conversation?.id) return null;
  const messages = conversation.messages || [];
  const assistantIndex = findLatestAssistantMessageIndex(messages);
  if (assistantIndex < 0) return null;
  return buildExchangePinPayload(conversation, assistantIndex);
}

export function buildExchangePinPayload(conversation, assistantMessageIndex) {
  if (!conversation?.id) return null;
  const messages = conversation.messages || [];
  const assistant = messages[assistantMessageIndex];
  if (!assistant || assistant.role !== 'assistant') return null;

  const user = findPrecedingUserMessage(messages, assistantMessageIndex);
  const sources = assistant.sources || [];

  return {
    conversationId: String(conversation.id),
    pinKind: 'exchange',
    messageAt: String(assistant.at || assistantMessageIndex),
    title: extractPinQuestionTitle(user?.content) || 'Question',
    snippet: truncateText(assistant.content || 'Answer', 180),
    imageUrl: firstSourceImage(sources),
    sourceUrl: firstSourceUrl(sources),
  };
}
